import React, { ReactElement, useState } from 'react';
import { UiNodeGroupEnum, UiNodeTypeEnum, UiNodeInputAttributesTypeEnum } from '@ory/kratos-client';
import { LoginFlow } from '@ory/kratos-client/api';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams, useLoaderData, Link, useNavigate } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Loader } from 'common/components/Loader/Loader';
import { authCreateLoginFlow, authUpdateLoginFlow } from 'domains/auth/services/auth.service';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SCard, SWrapper, SErrorMessage } from './login.page.styles';
import { TLoginPageLoaderData } from './types/login.types';

const LoginPage = (): ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const from = searchParams.get('from') || ROUTES_MAPPING.ROOT;
  const { loginFlow, initialValues } = useLoaderData() as TLoginPageLoaderData;
  const [isLoginFormSubmitLoading, setIsLoginFormSubmitLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formSubmitHandler = async (values: any): Promise<void> => {
    try {
      setErrorMessage('');
      const formFieldsError = Object.keys(initialValues).map((fieldName) => ({
        name: fieldName,
        errors: [],
      }));
      form.setFields(formFieldsError);
      setIsLoginFormSubmitLoading(true);

      const { csrf_token, identifier, password } = values;

      const requestLoginFlowData = {
        flow: loginFlow.id,
        updateLoginFlowBody: {
          csrf_token,
          identifier,
          password,
          method: 'password',
        },
      };
      await authUpdateLoginFlow(requestLoginFlowData);

      if (from.length > 1) {
        navigate(from);
      } else {
        navigate(ROUTES_MAPPING.PROTECTED.DASHBOARD);
      }
    } catch (error: any) {
      console.error(error);
      setIsLoginFormSubmitLoading(false);

      const { data } = error.response;

      setErrorMessage(data?.ui?.messages?.[0]?.text || '');

      const formFieldsError = Object.keys(initialValues).map((fieldName) => ({
        name: fieldName,
        errors: [''],
      }));
      form.setFields(formFieldsError);
    }
  };

  const renderNode = (node: any, index: number): null | ReactElement => {
    if (node.group === UiNodeGroupEnum.Oidc) {
      // skip oidc group.
      return null;
    }

    if (node.type === UiNodeTypeEnum.Input) {
      if (node.group === UiNodeGroupEnum.Default) {
        if (node.attributes.type === UiNodeInputAttributesTypeEnum.Text) {
          if (node.attributes.name === 'identifier') {
            return (
              <Form.Item
                key={index}
                name="identifier"
                rules={[
                  {
                    required: node.attributes.required,
                    message: t('auth.login.form.validation.required')!,
                  },
                ]}
              >
                <Input
                  size="large"
                  type={node.attributes.type}
                  allowClear
                  placeholder={t(`auth.login.input.identifier`) as string}
                  dataTestId={`${node.attributes.name}_input`}
                />
              </Form.Item>
            );
          }
        }

        if (node.attributes.type === UiNodeInputAttributesTypeEnum.Hidden) {
          return (
            <Form.Item key={index} name={node.attributes.name} hidden>
              <Input size="large" type={node.attributes.type} dataTestId={`${node.attributes.name}_input`} />
            </Form.Item>
          );
        }
      }

      if (node.group === UiNodeGroupEnum.Password) {
        if (node.attributes.type === UiNodeInputAttributesTypeEnum.Password) {
          return (
            <Form.Item
              key={index}
              name="password"
              rules={[
                {
                  required: node.attributes.required,
                  message: t('auth.login.form.validation.required')!,
                },
              ]}
            >
              <Input.Password
                size="large"
                type={node.attributes.type}
                allowClear
                placeholder={t(`auth.login.input.password`) as string}
                dataTestId={`${node.attributes.name}_input`}
              />
            </Form.Item>
          );
        }

        if (node.attributes.type === UiNodeInputAttributesTypeEnum.Submit) {
          return (
            <div key="submit">
              {!!errorMessage && <SErrorMessage>{errorMessage}</SErrorMessage>}
              <SWrapper>
                <div>{/* <Checkbox>{t('auth.login.checkbox.rememberMe')}</Checkbox> */}</div>
                <Link to={ROUTES_MAPPING.RESET_PASSWORD}>{t('auth.login.button.forgotPassword')}</Link>
              </SWrapper>
              <Button
                value={node.attributes.value}
                type="primary"
                dataTestId="submit_button"
                loading={isLoginFormSubmitLoading}
                size="large"
                htmlType="submit"
                block
              >
                <Trans i18nKey="auth.login.button.signIn" />
              </Button>
            </div>
          );
        }
      }
    }

    return null;
  };

  return !loginFlow ? (
    <Loader />
  ) : (
    <SCard>
      <Form initialValues={initialValues} form={form} dataTestId="signin_form" onFinish={formSubmitHandler}>
        {loginFlow.ui.nodes.map(renderNode)}
      </Form>
    </SCard>
  );
};

export const loader = async (): Promise<{initialValues: any, loginFlow: LoginFlow} | {}> => {
  try {
    const loginFlow = await authCreateLoginFlow();
    const initialValues = loginFlow.ui.nodes.reduce(
      (acc: any, node: any) => ({
        ...acc,
        [node.attributes.name]: node.attributes.value,
      }),
      {},
    );

    return {
      loginFlow,
      initialValues,
    };
  } catch (error: any) {
    return {};
  }
};

export default LoginPage;
